import "./AddStainOrderPanel.css";

import { Field, Form, Formik } from "formik";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { CaseAdmin, ExternalLabStain } from "pwapi";

import { stainOrderRequest } from "shared/actions/caseDetailActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { useSortedBlocks } from "shared/hooks/useSortedCase";
import selectLabStains from "shared/selectors/selectLabStains";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";
import { useAppSelector } from "store/hooks";

import AdminPanel from "../adminPanel/AdminPanel";
import { AdminFieldLabStainType } from "../edit/fields";
import HalfScreenPanel from "../editPanels/halfScreenEditPanel/HalfScreenPanel";
import { LabStainValue } from "../types/adminTypes";

type AddStainOrderPanelProps = {
  adminCase: CaseAdmin;
  returnUrl: string;
};

type StainOrderFormValues = {
  block_id: string;
  stain_id: string;
  note?: string;
};

const AddStainOrderPanel = ({
  adminCase,
  returnUrl,
}: AddStainOrderPanelProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const blocks = useSortedBlocks(adminCase);
  const availableLabStains = useAppSelector((state) =>
    selectLabStains(state, adminCase.accessioning_lab?.id || 0)
  );

  const isLoading = useIsLoading(stainOrderRequest, {
    requestBody: { case_id: adminCase?.id },
  });

  const stainsById = useMemo(() => {
    return [...availableLabStains]
      .sort((a, b) => (a.stain?.id || 0) - (b.stain?.id || 0))
      .reduce((acc, labStain) => {
        acc[String(labStain.id)] = {
          id: labStain.stain.id,
          lab_stain_id: labStain.id,
          category: labStain.stain?.category,
          name: labStain.stain?.name || labStain.label_override || "",
          abbreviation: labStain.stain?.abbreviation || "",
          is_archived: labStain.is_archived,
          external_stain: labStain.external_stain as ExternalLabStain,
        };
        return acc;
      }, {} as Record<string, LabStainValue>);
  }, [availableLabStains]);

  const handleOnSubmit = (values: StainOrderFormValues) => {
    const blockId = Number(values.block_id);
    const labStainId = Number(values.stain_id);

    if (!blockId || !labStainId || !adminCase.id) return;

    dispatch(
      stainOrderRequest({
        requestBody: {
          case_id: adminCase.id,
          block_id: blockId,
          lab_stain_id: labStainId,
          slide_count: 1,
          note: values.note || undefined,
        },
      })
    ).then(() => {
      onClose();
    });
  };

  const onClose = () => {
    navigate(returnUrl, { replace: true });
  };

  return (
    <HalfScreenPanel onClose={onClose}>
      <div className="AddStainOrderPanel">
        <Formik<StainOrderFormValues>
          initialValues={{
            block_id: "",
            stain_id: "",
            note: "",
          }}
          onSubmit={handleOnSubmit}
        >
          {({ values, setValues }) => (
            <Form className="AdminPanelForm">
              <AdminPanel
                title={{
                  prefix: `Add Slide To ${adminCase.accession}`,
                  main: "Add New Order",
                }}
              >
                <>
                  <div className="panelContainer">
                    <fieldset className="panelContainer__section">
                      <legend className="panelContainer__section--title">
                        Stain Order Details
                      </legend>
                      <div className="panelContainer__inputGroup">
                        <CustomInput label="Accession">
                          <div className="flatField">{adminCase.accession}</div>
                        </CustomInput>
                        <Field name="block_id">
                          {({ field }: any) => (
                            <SelectInput
                              label="Block"
                              id="block_id"
                              options={blocks.map((block) => ({
                                label: block.full_name || "",
                                value: String(block.id),
                              }))}
                              {...field}
                              required
                            />
                          )}
                        </Field>
                      </div>
                      <Field name="stain_id">
                        {({ field }: any) => (
                          <AdminFieldLabStainType
                            {...field}
                            onChange={(newValue: { object: LabStainValue }) => {
                              setValues({
                                ...values,
                                stain_id: String(newValue.object.lab_stain_id),
                              });
                            }}
                            required
                            allStains={stainsById}
                            availableStains={stainsById}
                          />
                        )}
                      </Field>
                      <Field name="note">
                        {({ field }: any) => (
                          <TextInput
                            label="Note"
                            id="note"
                            placeholder="Add a note..."
                            {...field}
                          />
                        )}
                      </Field>
                    </fieldset>
                    <ul className="panelContainer__warning form__warningList">
                      <li>
                        Submitted orders will take 5 minutes to populate at the
                        microtomy station
                      </li>
                    </ul>
                  </div>
                  <footer>
                    <PathologyWatchButton onClick={onClose}>
                      Cancel
                    </PathologyWatchButton>
                    <PathologyWatchButton
                      type="submit"
                      color="green"
                      disabled={
                        !values.block_id || !values.stain_id || isLoading
                      }
                      displaySpinner={isLoading}
                    >
                      Submit Order
                    </PathologyWatchButton>
                  </footer>
                </>
              </AdminPanel>
            </Form>
          )}
        </Formik>
      </div>
    </HalfScreenPanel>
  );
};

export default AddStainOrderPanel;
